import React, { useState } from 'react';
import { 
  BarChart3, 
  FileText, 
  Users, 
  Building, 
  Calendar, 
  Home, 
  CreditCard, 
  Vote,
  TrendingUp,
  Download,
  Filter
} from 'lucide-react';
import ReportsDashboard from './ReportsDashboard';
import ComplaintReportsPage from './ComplaintReportsPage';

type ReportType = 'dashboard' | 'complaints' | 'users' | 'departments' | 'events' | 'businesses' | 'charges' | 'elections';

const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState<ReportType>('dashboard');

  const reportTypes = [
    { id: 'dashboard', name: 'Dashboard', icon: BarChart3, description: 'Overview of all metrics' },
    { id: 'complaints', name: 'Complaints', icon: FileText, description: 'Complaint analysis and trends' },
    { id: 'users', name: 'Users', icon: Users, description: 'User statistics and growth' },
    { id: 'departments', name: 'Departments', icon: Building, description: 'Department performance' },
    { id: 'events', name: 'Events', icon: Calendar, description: 'Event management reports' },
    { id: 'businesses', name: 'Businesses', icon: Home, description: 'Business directory reports' },
    { id: 'charges', name: 'Charges', icon: CreditCard, description: 'Financial reports' },
    { id: 'elections', name: 'Elections', icon: Vote, description: 'Election statistics' }
  ];

  const renderReport = () => {
    switch (activeReport) {
      case 'dashboard':
        return <ReportsDashboard />;
      case 'complaints':
        return <ComplaintReportsPage />;
      case 'users':
        return <div className="p-8 text-center"><Users className="mx-auto h-12 w-12 text-gray-400 mb-4" /><p>User reports coming soon...</p></div>;
      case 'departments':
        return <div className="p-8 text-center"><Building className="mx-auto h-12 w-12 text-gray-400 mb-4" /><p>Department reports coming soon...</p></div>;
      case 'events':
        return <div className="p-8 text-center"><Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" /><p>Event reports coming soon...</p></div>;
      case 'businesses':
        return <div className="p-8 text-center"><Home className="mx-auto h-12 w-12 text-gray-400 mb-4" /><p>Business reports coming soon...</p></div>;
      case 'charges':
        return <div className="p-8 text-center"><CreditCard className="mx-auto h-12 w-12 text-gray-400 mb-4" /><p>Charge reports coming soon...</p></div>;
      case 'elections':
        return <div className="p-8 text-center"><Vote className="mx-auto h-12 w-12 text-gray-400 mb-4" /><p>Election reports coming soon...</p></div>;
      default:
        return <ReportsDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="mt-2 text-gray-600">
            Comprehensive insights and analytics for NFCRWA management
          </p>
        </div>

        {/* Report Type Selector */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reportTypes.map((report) => {
              const Icon = report.icon;
              const isActive = activeReport === report.id;
              
              return (
                <button
                  key={report.id}
                  onClick={() => setActiveReport(report.id as ReportType)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isActive
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className={`h-6 w-6 mr-3 ${
                      isActive ? 'text-blue-500' : 'text-gray-400'
                    }`} />
                    <div className="text-left">
                      <h3 className={`text-sm font-medium ${
                        isActive ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {report.name}
                      </h3>
                      <p className={`text-xs ${
                        isActive ? 'text-blue-700' : 'text-gray-500'
                      }`}>
                        {report.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Report Content */}
        {renderReport()}
      </div>
    </div>
  );
};

export default ReportsPage;
